import { type Product, type ProfitStatDays, profitStat } from '../../../data/product';
import { shortCurrencyFormatter } from '../../../helpers/format-currency';
import { defineColumn, type StrictColumnDef } from '../../tanstack-table/column';

/**
 * Gross profit the product actually made over the last `days`, summed on the
 * backend from each sale line's own price, discount and cost. Nothing here is
 * estimated: promos, singles-vs-pack pricing and cost changes during the window
 * are all already in the number.
 *
 * Labelled with its window ("90d Profit") by default, like Cases Sold. Pass a
 * fixed `id` where the window follows a filter, so a sort on the column
 * survives the window changing, and a `header` where a caller's label is
 * pinned by what its users already know.
 */
export function getProfitColumn(
  days: ProfitStatDays,
  { id, header = `${days}d Profit` }: { id?: string; header?: string } = {}
): StrictColumnDef<Product> {
  return defineColumn<Product>({
    id: id ?? header,
    header,
    minWidth: 70,
    accessorFn: (product) => shortCurrencyFormatter(profitStat(product, days)),
    toCSV: (product) => ({ [header]: profitStat(product, days).toFixed(2) }),
    sortKeyFn: (product) => profitStat(product, days),
    gloss: `Gross profit from the last ${days} days of sales: what each sale brought in, less its cost at the time.`,
  });
}
